import {
  ensurePicqerKeyPool,
  getLocationStockHistoryPage,
  getLocationsPage,
  picqerKeyPool,
} from '../../services/picqerAnalyseService';
import type { PicqerLocation, PicqerLocationStockHistoryEntry } from './types';
import {
  ANALYSE_SINCE_DATE,
  ANALYSE_UNTIL_DATE,
  HISTORY_WORKERS_PER_KEY,
  LOCATIONS_FETCH_CONCURRENCY,
} from './types';

async function runPool<T>(
  items: T[],
  worker: (item: T, workerIndex: number) => Promise<void>,
  concurrency: number,
  signal?: AbortSignal,
): Promise<void> {
  if (items.length === 0) return;
  let nextIndex = 0;

  async function runWorker(workerIndex: number): Promise<void> {
    for (;;) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const index = nextIndex;
      nextIndex += 1;
      if (index >= items.length) return;
      await worker(items[index], workerIndex);
    }
  }

  const workers = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workers }, (_, i) => runWorker(i)));
}

export async function fetchAllLocationsForWarehouse(
  idwarehouse: number,
  onProgress?: (loaded: number, page: number) => void,
  signal?: AbortSignal,
  keySlot?: number,
): Promise<PicqerLocation[]> {
  const all: PicqerLocation[] = [];
  let offset = 0;
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const result = await getLocationsPage(idwarehouse, offset, 100, keySlot);
    if (!result.success || result.data === undefined) {
      throw new Error(result.error ?? 'Failed to load locations');
    }
    all.push(...result.data);
    page += 1;
    onProgress?.(all.length, page);
    hasMore = result.hasMore ?? result.data.length === 100;
    offset += 100;
  }

  return all;
}

export async function fetchAllLocations(
  warehouseIds: number[],
  onWarehouseProgress?: (idwarehouse: number, loaded: number, page: number) => void,
  signal?: AbortSignal,
): Promise<Record<number, PicqerLocation[]>> {
  await ensurePicqerKeyPool();
  const result: Record<number, PicqerLocation[]> = {};

  await runPool(
    warehouseIds,
    async (idwarehouse, workerIndex) => {
      const keySlot = picqerKeyPool.slotForWorker(workerIndex);
      result[idwarehouse] = await fetchAllLocationsForWarehouse(
        idwarehouse,
        (loaded, page) => onWarehouseProgress?.(idwarehouse, loaded, page),
        signal,
        keySlot,
      );
    },
    LOCATIONS_FETCH_CONCURRENCY,
    signal,
  );

  return result;
}

async function fetchHistoryPages(
  idlocation: number,
  sincedate: string | null | undefined,
  untildate: string | null | undefined,
  signal?: AbortSignal,
  keySlot?: number,
): Promise<PicqerLocationStockHistoryEntry[]> {
  const all: PicqerLocationStockHistoryEntry[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const result = await getLocationStockHistoryPage(idlocation, {
      sincedate: sincedate ?? undefined,
      untildate: untildate ?? undefined,
      offset,
      keySlot,
    });
    if (!result.success || result.data === undefined) {
      throw new Error(result.error ?? 'Failed to load stock history');
    }
    all.push(...result.data);
    hasMore = result.hasMore ?? result.data.length === 100;
    offset += 100;
  }

  return all;
}

export async function fetchPreRangeStock(
  idlocation: number,
  signal?: AbortSignal,
  keySlot?: number,
): Promise<number> {
  const result = await getLocationStockHistoryPage(idlocation, {
    sincedate: null,
    untildate: ANALYSE_SINCE_DATE,
    offset: 0,
    limit: 100,
    keySlot,
  });
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (!result.success || !result.data?.length) return 0;

  const sorted = [...result.data].sort(
    (a, b) => new Date(a.changed_at.replace(' ', 'T')).getTime() - new Date(b.changed_at.replace(' ', 'T')).getTime(),
  );
  return sorted[sorted.length - 1].new_stock;
}

export async function fetchLocationHistoryWithProbe(
  idlocation: number,
  signal?: AbortSignal,
  keySlot?: number,
): Promise<{ history: PicqerLocationStockHistoryEntry[]; preRangeStock: number }> {
  const preRangeStock = await fetchPreRangeStock(idlocation, signal, keySlot);
  const history = await fetchHistoryPages(
    idlocation,
    ANALYSE_SINCE_DATE,
    ANALYSE_UNTIL_DATE,
    signal,
    keySlot,
  );
  return { history, preRangeStock };
}

export interface HistoryLoadProgress {
  completed: number;
  total: number;
  currentLocationId: number | null;
}

export async function fetchAllLocationHistory(
  locations: PicqerLocation[],
  existingHistory: Record<number, PicqerLocationStockHistoryEntry[]>,
  onProgress: (progress: HistoryLoadProgress) => void,
  onLocationDone: (idlocation: number, history: PicqerLocationStockHistoryEntry[], preRangeStock: number) => void,
  signal?: AbortSignal,
  options?: { forceRefresh?: boolean },
): Promise<void> {
  await ensurePicqerKeyPool();
  const concurrency = picqerKeyPool.recommendedConcurrency();

  const pending = options?.forceRefresh
    ? locations
    : locations.filter((l) => !existingHistory[l.idlocation]);
  const total = locations.length;
  let completed = locations.length - pending.length;

  onProgress({ completed, total, currentLocationId: null });

  await runPool(
    pending,
    async (loc, workerIndex) => {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const keySlot = picqerKeyPool.slotForWorker(workerIndex);
      onProgress({ completed, total, currentLocationId: loc.idlocation });
      const { history, preRangeStock } = await fetchLocationHistoryWithProbe(loc.idlocation, signal, keySlot);
      onLocationDone(loc.idlocation, history, preRangeStock);
      completed += 1;
      onProgress({ completed, total, currentLocationId: loc.idlocation });
    },
    concurrency,
    signal,
  );
}

export function getHistoryFetchConcurrency(): number {
  return picqerKeyPool.recommendedConcurrency();
}

export function getApiKeyPoolSize(): number {
  return picqerKeyPool.size;
}
